import { useConfig } from 'config/use-config';
import { ShopId } from 'constants/shop';
import { createContext, PropsWithChildren, FC, useCallback, useContext, useEffect } from 'react';
import { useUserSegmentationStore } from 'stores/user-segmentation';
import { Gtm } from 'tracking';
import convertProfileToArray from 'utils/pet-profile/convert-profile-to-array';
import { useSegmentationContext } from './segmentation';
import { useRootStore } from 'stores/use-root-store';
import { PetType } from 'constants/pet-type';
import { CatProfile, DogProfile } from 'types/pet-profile';

interface GaSegmentation {
  /** Last pushed segmentation  */
  lastSegment?: string;
  /** petProfile, joined string of segmentation   */
  petProfile: string;
  /** allSegments, joined string of segmentation   */
  allSegments: string;
}

interface TrackingContextType {
  trackHotjarEvent: (eventName?: string, eventProperties?: Record<string, unknown>) => void;
}

const TrackingContext = createContext<TrackingContextType>({} as any);

/**
 * A context used to track list names for performance analysis purposes
 */
export const TrackingContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { shopId } = useConfig();
  const petType = useRootStore((store) => store.petType);
  const currentPetProfile = useRootStore((store) => store.currentPetProfile);
  const customerPets = useRootStore((store) => store.customerPets);
  const { dogProfiles, catProfiles } = customerPets.reduce<{
    dogProfiles: DogProfile[];
    catProfiles: CatProfile[];
  }>(
    (acc, item) => {
      if (item.id === currentPetProfile.id) return acc;
      if (item.petType === PetType.Dog) {
        acc.dogProfiles.push(item);
      }
      if (item.petType === PetType.Cat) {
        acc.catProfiles.push(item);
      }
      return acc;
    },
    {
      dogProfiles: [],
      catProfiles: [],
    }
  );

  const dogProfile = dogProfiles[0];
  const catProfile = catProfiles[0];

  /**
   * Push segmentation to gtm
   */
  const { multipleSegmentation, lastSegmentation, allPetSegments } = useSegmentationContext();
  const { segments: userSegments } = useUserSegmentationStore();

  useEffect(() => {
    const dogProfileArray = dogProfile ? convertProfileToArray({ petProfile: dogProfile }) : [];
    const catProfileArray = catProfile ? convertProfileToArray({ petProfile: catProfile }) : [];

    const newVal: GaSegmentation = {
      allSegments: [
        ...new Set([...dogProfileArray, ...catProfileArray, ...userSegments, ...allPetSegments]),
      ]
        .sort()
        .join(','),
      petProfile: [...new Set([...dogProfileArray, ...catProfileArray, ...allPetSegments])]
        .sort()
        .join(','),
      lastSegment: lastSegmentation,
    };

    Gtm.session({
      ...newVal,
      ...(petType ? { petType } : {}),
    });
  }, [
    catProfile,
    dogProfile,
    lastSegmentation,
    multipleSegmentation,
    userSegments,
    allPetSegments,
    petType,
  ]);

  /**
   * Utility function to trigger Hotjar events
   * It will only trigger the event if the cookie value is 'dogs'
   * Mainly used to track user behavior with the Dynamic UX
   */
  const trackHotjarEvent = useCallback(
    (eventName: string = 'start_hotjar', eventProperties?: Record<string, unknown>): void => {
      try {
        // Check if Hotjar is available
        if (typeof window !== 'undefined' && window.hj) {
          if (eventProperties) {
            window.hj('event', eventName, eventProperties);
          } else {
            console.log('Tracking Hotjar event:', eventName);
            window.hj('event', eventName);
          }
        }
      } catch (error) {
        console.error('Error tracking Hotjar event:', error);
      }
    },
    []
  );

  /**
   * Track Hotjar event for CH
   */
  useEffect(() => {
    if (shopId === ShopId.CH) {
      trackHotjarEvent('pdex_ch');
    }
  }, [shopId, trackHotjarEvent]);

  return (
    <TrackingContext.Provider
      value={{
        trackHotjarEvent,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};

export const useTrackingContext = (): TrackingContextType => useContext(TrackingContext);
